'use client'

import { useState, useContext, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/shadcn/button'
import { Checkbox } from '@/components/ui/shadcn/checkbox'
import { Switch } from '@/components/ui/shadcn/switch'
import { Label } from '@/components/ui/shadcn/label'
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/shadcn/select'
import { CheckCircle2, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { AuthContext } from '@/contexts/AuthContext'
import { fetcher } from '@/lib/api/fetch'
import { SEVERITIES, CHANNELS } from '@/lib/notifications'

const COMMON_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Australia/Sydney',
]

export default function NotificationPreferencesCard({
  authContext: authContextProp,
  org: orgProp,
} = {}) {
  const authContextValue = useContext(AuthContext)
  const authContext = authContextProp || authContextValue
  const org = orgProp || authContext?.user?.organization
  const orgId = org?.id

  const [muted, setMuted] = useState(false)
  const [overrides, setOverrides] = useState({}) // { severity: [channels] } (only overridden severities)
  const [qhEnabled, setQhEnabled] = useState(false)
  const [qhStart, setQhStart] = useState('22:00')
  const [qhEnd, setQhEnd] = useState('08:00')
  const [tz, setTz] = useState('UTC')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    if (!orgId) return
    setLoading(true)
    setError(null)
    try {
      const res = await fetcher(
        `/api/admin/organization/${orgId}/notification-preferences`,
        { method: 'GET' },
        authContext,
      )
      const data = await res.json()
      if (!res.ok) {
        setError(data.error || 'Failed to load')
        return
      }
      setMuted(!!data.muted)
      setOverrides(data.severityChannels || {})
      setQhEnabled(!!data.quietHoursEnabled)
      if (data.quietHoursStart) setQhStart(data.quietHoursStart)
      if (data.quietHoursEnd) setQhEnd(data.quietHoursEnd)
      if (data.timezone) setTz(data.timezone)
    } catch (e) {
      setError(e.message || 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [orgId, authContext])

  useEffect(() => {
    load()
  }, [load])

  const isOverridden = sev =>
    Object.prototype.hasOwnProperty.call(overrides, sev)
  const toggleOverride = sev =>
    setOverrides(prev => {
      const next = { ...prev }
      if (isOverridden(sev)) delete next[sev]
      else next[sev] = []
      return next
    })
  const toggleChannel = (sev, ch) =>
    setOverrides(prev => {
      const cur = new Set(prev[sev] || [])
      cur.has(ch) ? cur.delete(ch) : cur.add(ch)
      return { ...prev, [sev]: Array.from(cur) }
    })

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const res = await fetcher(
        `/api/admin/organization/${orgId}/notification-preferences`,
        {
          method: 'PUT',
          body: JSON.stringify({
            muted,
            severityChannels: overrides,
            quietHoursEnabled: qhEnabled,
            quietHoursStart: qhStart,
            quietHoursEnd: qhEnd,
            timezone: tz,
          }),
        },
        authContext,
      )
      const data = await res.json()
      if (!res.ok) {
        setError(data.error || 'Failed to save')
        return
      }
      setSaved(true)
      setTimeout(() => setSaved(false), 8000)
      toast.success('Preferences saved')
    } catch (e) {
      setError(e.message || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-4">
        <h3 className="text-sm font-semibold">My Notification Preferences</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Override the org defaults for your account, mute everything, or set
          quiet hours (real-time alerts pause; the bell and digests catch up —
          critical always breaks through).
        </p>
      </div>

      {saved && (
        <Alert className="mb-3 border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200">
          <CheckCircle2 className="size-4" />
          <AlertDescription>Preferences saved</AlertDescription>
        </Alert>
      )}
      {error && (
        <Alert variant="destructive" className="mb-3">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <div className="flex items-center gap-3">
            <Switch id="mute" checked={muted} onCheckedChange={setMuted} />
            <Label htmlFor="mute">Mute all notifications</Label>
          </div>

          <div className="flex flex-col gap-2">
            <div className="text-sm font-medium">Per-severity overrides</div>
            {SEVERITIES.map(sev => (
              <div key={sev.value} className="rounded-md border p-3">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={isOverridden(sev.value)}
                    onCheckedChange={() => toggleOverride(sev.value)}
                  />
                  <Label>{sev.label} — override org default</Label>
                </div>
                {isOverridden(sev.value) && (
                  <div className="mt-2 flex flex-wrap gap-4 pl-10">
                    {CHANNELS.map(ch => (
                      <label
                        key={ch.value}
                        className="flex items-center gap-2 text-sm"
                      >
                        <Checkbox
                          checked={(overrides[sev.value] || []).includes(
                            ch.value,
                          )}
                          onCheckedChange={() =>
                            toggleChannel(sev.value, ch.value)
                          }
                        />
                        {ch.label}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <Switch
                id="qh"
                checked={qhEnabled}
                onCheckedChange={setQhEnabled}
              />
              <Label htmlFor="qh">Enable quiet hours</Label>
            </div>
            {qhEnabled && (
              <div className="flex flex-wrap items-end gap-4 pl-10">
                <div className="flex flex-col gap-1">
                  <Label>From</Label>
                  <input
                    type="time"
                    value={qhStart}
                    onChange={e => setQhStart(e.target.value)}
                    className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label>To</Label>
                  <input
                    type="time"
                    value={qhEnd}
                    onChange={e => setQhEnd(e.target.value)}
                    className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label>Timezone</Label>
                  <Select value={tz} onValueChange={setTz}>
                    <SelectTrigger className="w-56">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COMMON_TIMEZONES.map(z => (
                        <SelectItem key={z} value={z}>
                          {z}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
